import { useCallback, useState } from 'react';

import type { LayoutChangeEvent } from 'react-native';
import {
  Linking,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import Feather from '@expo/vector-icons/Feather';
import Toast from 'react-native-toast-message';

import { useSofiaTime } from '../hooks/use-sofia-time';
import { colors } from '../theme';
import { inter400, inter500, isWeb } from '../utils/platform';

type FormStatus = 'error' | 'idle' | 'submitting' | 'success';
type ContactFormValues = {
  email: string;
  message: string;
  name: string;
  website: string;
};
type ContactFormField = 'email' | 'message' | 'name';
type ContactFormErrors = Partial<Record<ContactFormField, string>>;
type ContactFormTouched = Partial<Record<ContactFormField, boolean>>;

type ContactSectionProps = {
  copiedEmail: boolean;
  email: string;
  isMobile: boolean;
  linkedInUrl: string;
  onCopyEmail: () => void;
  onLayout: (event: LayoutChangeEvent) => void;
  onPressPhone: () => void;
  onPressTitle: () => void;
  phone: string;
};

const CONTACT_API_PATH = '/api/contact';
const INITIAL_VALUES: ContactFormValues = {
  email: '',
  message: '',
  name: '',
  website: '',
};
const EMAIL_REGEX = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;

const validateForm = (values: ContactFormValues): ContactFormErrors => {
  const errors: ContactFormErrors = {};
  const trimmedName = values.name.trim();
  const trimmedEmail = values.email.trim();
  const trimmedMessage = values.message.trim();

  if (trimmedName.length < 2) {
    errors.name = 'Name must be at least 2 characters.';
  } else if (trimmedName.length > 100) {
    errors.name = 'Name must be at most 100 characters.';
  }

  if (!EMAIL_REGEX.test(trimmedEmail)) {
    errors.email = 'Please enter a valid email address.';
  } else if (trimmedEmail.length > 254) {
    errors.email = 'Email address is too long.';
  }

  if (trimmedMessage.length < 10) {
    errors.message = 'Message must be at least 10 characters.';
  } else if (trimmedMessage.length > 2000) {
    errors.message = 'Message must be at most 2000 characters.';
  }

  return errors;
};

export function ContactSection({
  copiedEmail,
  email,
  isMobile,
  linkedInUrl,
  onCopyEmail,
  onLayout,
  onPressPhone,
  onPressTitle,
  phone,
}: ContactSectionProps) {
  const sofiaTime = useSofiaTime();
  const [status, setStatus] = useState<FormStatus>('idle');
  const [values, setValues] = useState<ContactFormValues>(INITIAL_VALUES);
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [touched, setTouched] = useState<ContactFormTouched>({});

  const resetStatus = useCallback(() => {
    if (status !== 'idle') {
      setStatus('idle');
    }
  }, [status]);

  const showToast = useCallback((variant: 'error' | 'success', message: string) => {
    Toast.show({
      position: 'bottom',
      text1: message,
      type: variant,
      visibilityTime: 3600,
    });
  }, []);

  const onBlurField = useCallback(
    (field: ContactFormField) => () => {
      setTouched((previous) => ({ ...previous, [field]: true }));
      setErrors(validateForm(values));
    },
    [values],
  );

  const onChangeField = useCallback(
    (field: ContactFormField) => (value: string) => {
      resetStatus();
      setValues((previous) => ({ ...previous, [field]: value }));

      if (touched[field]) {
        const nextValues = { ...values, [field]: value };
        setErrors(validateForm(nextValues));
      }
    },
    [resetStatus, touched, values],
  );

  const onPressSendMessage = useCallback(() => {
    if (status === 'submitting') {
      return;
    }

    resetStatus();

    const nextErrors = validateForm(values);
    setTouched({ email: true, message: true, name: true });
    setErrors(nextErrors);

    const firstError = nextErrors.name ?? nextErrors.email ?? nextErrors.message;
    if (firstError) {
      setStatus('error');
      showToast('error', firstError);
      return;
    }

    void (async () => {
      setStatus('submitting');

      try {
        const response = await fetch(CONTACT_API_PATH, {
          body: JSON.stringify({
            email: values.email.trim(),
            message: values.message.trim(),
            name: values.name.trim(),
            website: values.website,
          }),
          headers: { 'content-type': 'application/json' },
          method: 'POST',
        });

        const payload = (await response.json().catch(() => null)) as
          | { error?: string; ok?: boolean }
          | null;

        if (!response.ok || !payload?.ok) {
          setStatus('error');
          showToast('error', payload?.error ?? 'Unable to send message right now.');
          return;
        }

        setStatus('success');
        showToast('success', 'Message sent successfully.');
        setErrors({});
        setTouched({});
        setValues(INITIAL_VALUES);
      } catch {
        setStatus('error');
        showToast('error', 'Unable to send message right now.');
      }
    })();
  }, [resetStatus, showToast, status, values]);

  const onChangeName = useCallback(
    (value: string) => onChangeField('name')(value),
    [onChangeField],
  );

  const onChangeEmail = useCallback(
    (value: string) => onChangeField('email')(value),
    [onChangeField],
  );

  const onChangeMessage = useCallback(
    (value: string) => onChangeField('message')(value),
    [onChangeField],
  );

  const nameError = touched.name ? errors.name : undefined;
  const emailError = touched.email ? errors.email : undefined;
  const messageError = touched.message ? errors.message : undefined;

  return (
    <View nativeID="contact" onLayout={onLayout} style={styles.contactSection}>
      <Pressable onPress={onPressTitle} style={styles.sectionTitleLink}>
        <Text style={styles.sectionTitle}>Let's talk</Text>
      </Pressable>

      <View style={[styles.contactContentRow, isMobile && styles.contactContentRowMobile]}>
        <View style={[styles.contactInfo, isMobile && styles.contactInfoMobile]}>
          <View style={styles.contactTimeRow}>
            <Text style={styles.contactLabelMuted}>Time for me:</Text>
            <Text style={styles.contactTime}>{sofiaTime}</Text>
          </View>

          <View style={styles.contactBlock}>
            <Text style={styles.contactLabel}>Email:</Text>
            <Pressable aria-label="Copy email" onPress={onCopyEmail} style={styles.contactEmailLink}>
              <Feather name="copy" size={16} color={colors.muted} />
              <Text style={styles.linkText}>{email}</Text>

              {copiedEmail ? (
                <View pointerEvents="none" style={styles.copyTooltip}>
                  <Text style={styles.copyTooltipText}>Copied</Text>
                </View>
              ) : null}
            </Pressable>
          </View>

          <View style={styles.contactBlock}>
            <Text style={styles.contactLabel}>Phone:</Text>
            <Pressable
              aria-label="Phone number"
              accessibilityRole="link"
              onPress={onPressPhone}
              style={styles.socialLink}
            >
              <Feather name="phone" size={16} color={colors.muted} />
              <Text style={styles.linkText}>{phone}</Text>
            </Pressable>
          </View>

          <View style={styles.contactBlock}>
            <Text style={styles.contactLabel}>Socials:</Text>
            <View style={styles.contactSocials}>
              <Pressable
                aria-label="LinkedIn"
                accessibilityRole="link"
                onPress={() => void Linking.openURL(linkedInUrl)}
                style={styles.socialLink}
              >
                <Feather name="linkedin" size={16} color={colors.muted} />
                <Text style={styles.linkText}>LinkedIn</Text>
              </Pressable>
            </View>
          </View>
        </View>

        <View style={[styles.contactForm, isMobile && styles.contactFormMobile]}>
          <Text style={styles.contactLabel}>Reach out:</Text>

          <View style={styles.contactFormFields}>
            <TextInput
              autoComplete="name"
              editable={status !== 'submitting'}
              onBlur={onBlurField('name')}
              onChangeText={onChangeName}
              placeholder="Your name"
              placeholderTextColor={colors.muted}
              style={[styles.input, styles.inputSingle]}
              value={values.name}
            />
            {nameError ? <Text style={styles.fieldErrorText}>{nameError}</Text> : null}

            <TextInput
              autoCapitalize="none"
              autoComplete="email"
              editable={status !== 'submitting'}
              keyboardType="email-address"
              onBlur={onBlurField('email')}
              onChangeText={onChangeEmail}
              placeholder="Your Email address"
              placeholderTextColor={colors.muted}
              style={[styles.input, styles.inputSingle]}
              value={values.email}
            />
            {emailError ? <Text style={styles.fieldErrorText}>{emailError}</Text> : null}

            <TextInput
              editable={status !== 'submitting'}
              multiline
              numberOfLines={6}
              onBlur={onBlurField('message')}
              onChangeText={onChangeMessage}
              placeholder="Message"
              placeholderTextColor={colors.muted}
              style={[styles.input, styles.textarea]}
              textAlignVertical="top"
              value={values.message}
            />
            {messageError ? <Text style={styles.fieldErrorText}>{messageError}</Text> : null}

            <Pressable
              aria-label="Send Message"
              onPress={onPressSendMessage}
              style={[styles.sendButton, status === 'submitting' && styles.sendButtonDisabled]}
            >
              <Text style={styles.sendButtonText}>
                {status === 'submitting' ? 'Sending...' : 'Send Message'}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  contactSection: {
    gap: 32,
    paddingBottom: 16,
    paddingTop: 30,
  },
  sectionTitleLink: {
    alignSelf: 'flex-start',
    ...(isWeb
      ? {
        cursor: 'pointer',
      }
      : null),
  },
  sectionTitle: {
    color: colors.text,
    fontFamily: inter500,
    fontSize: 20,
    fontWeight: '500',
    lineHeight: 24,
  },
  contactContentRow: {
    flexDirection: 'row',
    gap: 32,
    width: '100%',
  },
  contactContentRowMobile: {
    flexDirection: 'column',
  },
  contactInfo: {
    borderColor: colors.border,
    borderLeftWidth: 1,
    gap: 32,
    maxWidth: 240,
    paddingLeft: 16,
    width: 240,
  },
  contactInfoMobile: {
    maxWidth: '100%',
    width: '100%',
  },
  contactTimeRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    paddingBottom: 4,
    width: '100%',
  },
  contactLabelMuted: {
    color: colors.muted,
    fontFamily: inter500,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 19.2,
  },
  contactLabel: {
    color: colors.text,
    fontFamily: inter500,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 19.2,
  },
  contactTime: {
    color: colors.text,
    fontFamily: inter500,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 19.2,
    ...(isWeb
      ? {
        fontFeatureSettings: "'tnum'",
        fontVariantNumeric: 'tabular-nums',
      }
      : null),
  },
  contactBlock: {
    gap: 16,
    paddingBottom: 4,
    width: '100%',
  },
  contactEmailLink: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 6,
    height: 20,
    overflow: 'visible',
    paddingHorizontal: 4,
    position: 'relative',
  },
  socialLink: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 4,
  },
  linkText: {
    color: colors.muted,
    fontFamily: inter400,
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22.4,
  },
  copyTooltip: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 6,
    borderWidth: 1,
    left: 0,
    paddingHorizontal: 10,
    paddingVertical: 6,
    position: 'absolute',
    top: -36,
    zIndex: 20,
    ...(isWeb
      ? {
        boxShadow: '0px 8px 24px rgba(0,0,0,0.35)',
      }
      : null),
  },
  copyTooltipText: {
    color: colors.text,
    fontFamily: inter500,
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 14.4,
  },
  contactSocials: {
    gap: 16,
    width: '100%',
  },
  contactForm: {
    flex: 1,
    gap: 16,
  },
  contactFormMobile: {
    flex: 0,
    width: '100%',
  },
  contactFormFields: {
    gap: 20,
    width: '100%',
  },
  input: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    color: colors.text,
    fontFamily: inter400,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    width: '100%',
  },
  inputSingle: {
    height: 40,
  },
  textarea: {
    height: 180,
    minHeight: 180,
    ...(isWeb
      ? {
        resize: 'vertical',
      }
      : null),
  },
  sendButton: {
    alignItems: 'center',
    backgroundColor: colors.text,
    borderRadius: 6,
    height: 40,
    justifyContent: 'center',
    paddingHorizontal: 16,
    width: '100%',
  },
  sendButtonDisabled: {
    opacity: 0.6,
  },
  sendButtonText: {
    color: colors.background,
    fontFamily: inter400,
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22.4,
  },
  fieldErrorText: {
    color: '#ff6b6b',
    fontFamily: inter400,
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16.8,
    marginTop: -12,
  },
});
